import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'rc-cookie-consent';
const COOKIE_PREFS_KEY = 'rc-cookie-prefs';

type ConsentValue = 'accepted' | 'rejected' | 'custom' | null;
interface CookiePrefs {
  necessary: boolean;   // sempre true
  analytics: boolean;
  marketing: boolean;
}

const defaultPrefs: CookiePrefs = { necessary: true, analytics: false, marketing: false };

function loadPrefs(): CookiePrefs {
  try {
    const raw = localStorage.getItem(COOKIE_PREFS_KEY);
    return raw ? { ...defaultPrefs, ...JSON.parse(raw), necessary: true } : defaultPrefs;
  } catch { return defaultPrefs; }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>(defaultPrefs);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentValue;
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    } else {
      setHasConsent(true);
      setPrefs(loadPrefs());
    }
  }, []);

  function accept() {
    const allOn: CookiePrefs = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(allOn));
    setPrefs(allOn);
    setVisible(false);
    setShowSettings(false);
    setHasConsent(true);
  }

  function reject() {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(defaultPrefs));
    setPrefs(defaultPrefs);
    setVisible(false);
    setShowSettings(false);
    setHasConsent(true);
  }

  function saveCustom() {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'custom');
    localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(prefs));
    setVisible(false);
    setShowSettings(false);
    setHasConsent(true);
  }

  function modify() {
    setPrefs(loadPrefs());
    setVisible(true);
    setHasConsent(false);
  }

  // Mini floating button per modificare le preferenze
  if (!visible && hasConsent) {
    return (
      <button
        onClick={modify}
        aria-label="Modifica preferenze cookie"
        title="Modifica preferenze cookie"
        className="fixed bottom-4 left-4 z-50 w-10 h-10 rounded-full flex items-center justify-center
                   bg-surface-100/80 dark:bg-surface-800/80 backdrop-blur-sm
                   border border-surface-200 dark:border-surface-700
                   text-surface-500 dark:text-surface-400
                   hover:bg-surface-200 dark:hover:bg-surface-700
                   hover:text-surface-700 dark:hover:text-surface-200
                   shadow-sm transition-all duration-200"
      >
        <span className="text-sm">🍪</span>
      </button>
    );
  }

  if (!visible) return null;

  // ---------- Pannello Impostazioni Cookie ----------
  if (showSettings) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <div className="w-full max-w-md rounded-2xl border p-6
                        bg-white border-surface-200 shadow-elevated
                        dark:bg-[#242428] dark:border-surface-700">
          <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4">
            ⚙️ Impostazioni cookie
          </h3>

          <div className="space-y-4">
            {/* Necessari — sempre attivi */}
            <label className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-surface-800 dark:text-surface-200">Cookie necessari</p>
                <p className="text-xs text-surface-500 dark:text-surface-400">Essenziali per il funzionamento del sito. Sempre attivi.</p>
              </div>
              <input type="checkbox" checked disabled className="w-5 h-5 accent-primary-500 rounded" />
            </label>

            {/* Analytics */}
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div>
                <p className="text-sm font-medium text-surface-800 dark:text-surface-200">Cookie analitici</p>
                <p className="text-xs text-surface-500 dark:text-surface-400">Ci aiutano a capire come utilizzi il sito.</p>
              </div>
              <input
                type="checkbox"
                checked={prefs.analytics}
                onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                className="w-5 h-5 accent-primary-500 rounded"
              />
            </label>

            {/* Marketing */}
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div>
                <p className="text-sm font-medium text-surface-800 dark:text-surface-200">Cookie di marketing</p>
                <p className="text-xs text-surface-500 dark:text-surface-400">Utilizzati per mostrare contenuti pertinenti.</p>
              </div>
              <input
                type="checkbox"
                checked={prefs.marketing}
                onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                className="w-5 h-5 accent-primary-500 rounded"
              />
            </label>
          </div>

          <div className="flex items-center gap-3 mt-6 justify-end">
            <button onClick={reject} className="btn-ghost btn-sm">Rifiuta tutti</button>
            <button onClick={saveCustom} className="btn-primary btn-sm">Salva preferenze</button>
            <button onClick={accept} className="btn-primary btn-sm">Accetta tutti</button>
          </div>

          <p className="text-xs text-surface-400 dark:text-surface-500 mt-4 text-center">
            Leggi la nostra{' '}
            <Link to="/cookie-policy" className="underline hover:text-primary-500">Cookie Policy</Link>
            {' '}e la{' '}
            <Link to="/privacy" className="underline hover:text-primary-500">Privacy Policy</Link>
          </p>
        </div>
      </div>
    );
  }

  // ---------- Banner principale ----------

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 inset-x-0 z-50 p-4 md:p-6 animate-in"
    >
      <div className="max-w-4xl mx-auto rounded-2xl border p-5 sm:p-6
                      bg-white/95 border-surface-200 shadow-elevated backdrop-blur-lg
                      dark:bg-[#242428]/95 dark:border-surface-700 dark:shadow-none">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Testo */}
          <div className="flex-1 text-sm text-surface-600 dark:text-surface-300">
            <p className="font-medium text-surface-900 dark:text-white mb-1">
              🍪 Questo sito utilizza i cookie
            </p>
            <p>
              Utilizziamo cookie tecnici necessari al funzionamento e cookie analitici
              per migliorare la tua esperienza. Leggi la nostra{' '}
              <Link to="/privacy" className="text-primary-500 hover:text-primary-400 underline">
                Privacy Policy
              </Link>{' '}
              e la{' '}
              <Link to="/cookie-policy" className="text-primary-500 hover:text-primary-400 underline">
                Cookie Policy
              </Link>{' '}
              per saperne di più.
            </p>
          </div>

          {/* Azioni */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={reject}
              className="btn-ghost btn-sm"
            >
              Rifiuta
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="btn-ghost btn-sm"
            >
              ⚙️ Impostazioni
            </button>
            <button
              onClick={accept}
              className="btn-primary btn-sm"
            >
              Accetta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
